/* section-collection-par-couleur.js
 *
 * <color-filter-bar>   — Défini mais désactivé dans le Liquid. Réservé si les
 *                        boutons de filtre rapide sont réactivés.
 * <collection-filters> — Système de filtres complet (copie de filters.js).
 *                        Filtre basé sur les cartes [data-color] présentes dans
 *                        le DOM : renderSection() bascule en show/hide client-side
 *                        quand seuls des filtres filter.v.option.* sont actifs.
 *                        Tri et filtres prix/dispo conservent le fetch AJAX.
 */

(() => {
  'use strict';

  /* ── <color-filter-bar> ─────────────────────────────────────────────────── */

  if (!customElements.get('color-filter-bar')) {
    customElements.define('color-filter-bar', class extends HTMLElement {

      connectedCallback() {
        this._section = document.querySelector(`[data-section-id="${this.dataset.sectionId}"]`);
        if (!this._section) return;

        this._grid    = this._section.querySelector('[data-collection-products]');
        this._countEl = this._section.querySelector('[data-products-count]');
        this._active  = new Set();

        this._build();
      }

      _label(n) {
        return `${n} ${n === 1 ? 'produit' : 'produits'}`;
      }

      _build() {
        const cards  = this._cards();
        const colors = new Map();

        cards.forEach(card => {
          const handle = card.dataset.color;
          const label  = card.dataset.colorLabel || handle;
          if (handle && !colors.has(handle)) colors.set(handle, label);
        });

        if (this._countEl) {
          this._countEl.textContent = this._label(cards.length);
        }

        if (colors.size < 2) {
          this.hidden = true;
          return;
        }

        const ul = document.createElement('ul');
        ul.className = 'color-filter-bar__list';

        ul.appendChild(this._li('', 'Tout', true));
        colors.forEach((label, handle) => ul.appendChild(this._li(handle, label, false)));

        this.appendChild(ul);
        this.addEventListener('click', e => this._onClick(e));
      }

      _li(handle, label, active) {
        const li  = document.createElement('li');
        const btn = document.createElement('button');
        btn.type            = 'button';
        btn.className       = 'color-filter-bar__btn' + (active ? ' is-active' : '');
        btn.dataset.filterColor = handle;
        btn.textContent     = label;
        li.appendChild(btn);
        return li;
      }

      _onClick(e) {
        const btn = e.target.closest('[data-filter-color]');
        if (!btn) return;

        const handle = btn.dataset.filterColor;

        if (handle === '') {
          this._active.clear();
        } else {
          this._active.has(handle) ? this._active.delete(handle) : this._active.add(handle);
        }

        this._syncButtons();
        this._applyFilter();
      }

      _syncButtons() {
        const allVisible = this._active.size === 0;
        this.querySelectorAll('[data-filter-color]').forEach(btn => {
          const h = btn.dataset.filterColor;
          btn.classList.toggle('is-active', h === '' ? allVisible : this._active.has(h));
        });
      }

      _applyFilter() {
        const cards   = this._cards();
        let   visible = 0;

        cards.forEach(card => {
          const show = this._active.size === 0 || this._active.has(card.dataset.color);
          card.hidden = !show;
          if (show) visible++;
        });

        this._updateCount(visible, cards.length);
      }

      _updateCount(visible, total) {
        if (!this._countEl) return;
        this._countEl.textContent = visible >= total
          ? this._label(total)
          : `${visible} / ${total} produits`;
      }

      _cards() {
        return [...(this._grid?.querySelectorAll('[data-color]') ?? [])];
      }

    });
  }

  /* ── <collection-filters> ───────────────────────────────────────────────── */
  /*                                                                           */
  /* Copie de filters.js avec filtrage couleur client-side.                    */
  /* renderSection() utilise show/hide DOM quand seuls des filtres d'option    */
  /* couleur (filter.v.option.*) sont actifs. Tri et autres filtres            */
  /* (prix, disponibilité) conservent le fetch AJAX d'origine.                */

  if (!customElements.get('collection-filters')) {
    customElements.define('collection-filters', class extends HTMLElement {

      constructor() {
        super();
        this.sortDropdownEvent             = () => this.sortDropdownToggle();
        this.onTabHandlerEvent             = t => this.onTabHandler(t);
        this.updateCollectionFormSortEvent = t => this.updateCollectionFormSort(t);
        this.bodyClickEvent                = t => this.bodyClick(t);
        this.onFilterResetClick            = this.onFilterResetClick.bind(this);
        this.onFilterTagResetClick         = this.onFilterTagResetClick.bind(this);
        this.onFilterTagClearClick         = this.onFilterTagClearClick.bind(this);
        this.onFilterToggleClick           = this.onFilterToggleClick.bind(this);
        this.onKeyUpHandler                = this.onKeyUpHandler.bind(this);
        this.updateRangeEvent              = this.updateRange.bind(this);
        this.onHistoryChangeBound          = this.onHistoryChange.bind(this);
        this.debouncedSubmitEvent          = window.theme.debounce(t => this.onSubmitHandler(t), 500);
        this.debouncedSortEvent            = window.theme.debounce(t => this.onSortChange(t), 500);
      }

      connectedCallback() {
        this.container            = this.closest('[data-section-id]');
        this.sectionId            = this.container.dataset.sectionId;
        this.enableFilters        = this.container.dataset.enableFilters === 'true';
        this.enableSorting        = this.container.dataset.enableSorting === 'true';
        this.filterMode           = this.container.dataset.filterMode;
        this.collectionHandle     = this.container.dataset.collection;
        this.isSearchPage         = null != this.container.closest('[data-search-performed]');
        this.productGrid          = this.container.querySelector('[data-collection-products]');
        this.productsCount        = this.container.querySelector('[data-products-count]');
        this.groupTagFilters      = this.container.querySelectorAll('[data-collection-filter]');
        this.filters              = this.container.querySelector('[data-collection-filters]');
        this.filterTriggers       = this.container.querySelectorAll('[data-collapsible-trigger]');
        this.filtersStickyBar     = this.container.querySelector('[data-collection-sticky-bar]');
        this.filtersForm          = this.container.querySelector('[data-collection-filters-form]');
        this.inputSort            = this.container.querySelectorAll('[data-input-sort]');
        this.sortToggle           = this.container.querySelector('[data-sort-toggle]');
        this.collectionSortOptions = this.container.querySelector('[data-collection-sort-options]');
        this.ajaxinateItems       = this.container.querySelector('ajaxinate-items[data-collection-products]');
        this.a11y                 = window.theme.a11y;
        this.filterData           = [];

        // Snapshot des params non-couleur au chargement de la page.
        // Sert de référence pour détecter si le tri/dispo a réellement changé.
        this._baseQueryStr = this._getNonColorQueryStr(new URLSearchParams(window.location.search));

        if (this.filters) {
          this.hideFiltersDrawer = this.hideFiltersDrawer.bind(this);
          this.showFiltersDrawer = this.showFiltersDrawer.bind(this);
          this.resizeEvent = window.theme.debounce(() => this.filtersResizeEvents(), 500);
          this.filtersResizeEvents();
          document.addEventListener('theme:resize:width', this.resizeEvent);
        }

        this.initTagFilters();
        window.addEventListener('popstate', this.onHistoryChangeBound);
        this.bindToggleButtonsEvents();
        this.bindFilterButtonsEvents();
        this.initProductGridEvents();
        window.theme.setVars();
        this.sortToggle?.addEventListener('click', this.sortDropdownEvent);
        document.addEventListener('click', this.bodyClickEvent);
        this.filterShowMore();
      }

      /* ── Helpers filtrage client-side ──────────────────────────────────── */

      /** Convertit une chaîne en handle (minuscules, sans accents, tirets). */
      _handleize(str) {
        return str
          .toLowerCase()
          .normalize('NFD').replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      /** Correspondance filtre couleur → carte (3 niveaux de fallback). */
      _matchesColor(card, filterValue) {
        const needle = this._handleize(filterValue);

        if (card.dataset.color && card.dataset.color === needle) return true;

        if (card.dataset.colorLabel && this._handleize(card.dataset.colorLabel) === needle) return true;

        if (card.dataset.imageAlt && card.dataset.imageAlt.includes(needle)) return true;

        return false;
      }

      /**
       * Retourne une chaîne normalisée des params non-couleur (sort_by,
       * availability, price…). Les clés sont triées pour que la comparaison
       * soit indépendante de l'ordre des champs dans le formulaire vs l'URL.
       */
      _getNonColorQueryStr(params) {
        const base = new URLSearchParams();
        for (const [key, val] of params.entries()) {
          if (!key.startsWith('filter.v.option.')) base.append(key, val);
        }
        return new URLSearchParams(
          [...base.entries()].sort((a, b) => a[0].localeCompare(b[0]))
        ).toString();
      }

      /** Extrait les valeurs couleur (handleisées) des params filter.v.option.*. */
      _extractColorValues(params) {
        const colors = new Set();
        for (const [key, val] of params.entries()) {
          if (key.startsWith('filter.v.option.') && val) {
            colors.add(this._handleize(val));
          }
        }
        return colors;
      }

      /** Show/hide les cartes [data-color] et met à jour le compteur. */
      _filterCardsClientSide(colorValues) {
        const cards   = [...(this.productGrid?.querySelectorAll('[data-product-block]') ?? [])];
        let   visible = 0;

        cards.forEach(card => {
          const show = colorValues.size === 0
            || [...colorValues].some(v => this._matchesColor(card, v));
          card.hidden = !show;
          if (show) visible++;
        });

        if (this.productsCount) {
          this.productsCount.textContent = visible >= cards.length
            ? `${cards.length} ${cards.length === 1 ? 'produit' : 'produits'}`
            : `${visible} / ${cards.length} produits`;
        }
      }

      /* ── renderSection : client-side ou AJAX selon ce qui a réellement changé */

      renderSection(queryStr, evt, updateURL = true) {
        const params           = new URLSearchParams(queryStr);
        const nonColorStr      = this._getNonColorQueryStr(params);
        const onlyColorChanged = nonColorStr === this._baseQueryStr;

        if (onlyColorChanged) {
          // Seule la sélection couleur a changé → show/hide client-side
          // Pas de startLoading() : opération instantanée, aucun scroll forcé ni scroll-lock
          const colors = this._extractColorValues(params);
          this._filterCardsClientSide(colors);
          if (updateURL) this.updateURLHash(queryStr);
        } else {
          // Tri ou filtre non-couleur a changé → AJAX avec comportement complet
          this.startLoading();
          this._baseQueryStr = nonColorStr;
          const url  = `${window.location.pathname}?section_id=${this.sectionId}&${queryStr}`;
          const find = x => x.url === url;
          this.filterData.some(find)
            ? this.renderSectionFromCache(find, evt)
            : this.renderSectionFromFetch(url);
          if (updateURL) this.updateURLHash(queryStr);
        }
      }

      onHistoryChange(t) {
        if (!this.filters) return;
        let e = t.state?.searchParams || '';
        if (this.isSearchPage) {
          if (!t.state) e = window.location.search;
          if (!(e.indexOf('type=product') > -1)) return;
        }
        this.renderSection(e, null, false);
      }

      renderSectionFromFetch(url) {
        fetch(url)
          .then(r => r.text())
          .then(html => {
            this.filterData = [...this.filterData, { html, url }];
            this.inputSort  = this.container.querySelectorAll('[data-input-sort]');
            this.renderFilters(html);
            this.bindFilterButtonsEvents();
            this.hideFiltersOnMobile();
            this.renderProductGrid(html);
            this.updateProductsCount(html);
            this.finishLoading();
            this.mobileFiltersScrollLock();
          });
      }

      renderSectionFromCache(find) {
        const html = this.filterData.find(find).html;
        this.renderFilters(html);
        this.hideFiltersOnMobile();
        this.renderProductGrid(html);
        this.updateProductsCount(html);
        this.finishLoading();
        this.mobileFiltersScrollLock();
      }

      renderProductGrid(html) {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-collection-products]');
        if (!el) return;
        this.productGrid.innerHTML = el.innerHTML;
        this.reInitAjaxinate(el);
        this.initProductGridEvents();
        this.filterShowMore();
      }

      reInitAjaxinate(el) {
        if (el.matches('ajaxinate-items') && typeof this.ajaxinateItems?.reinit === 'function') {
          this.ajaxinateItems.reinit();
        }
      }

      initProductGridEvents() {
        this.handleVisibleTooltips();
        setTimeout(() => this.finishLoading(), 450);
      }

      updateProductsCount(html) {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-products-count]');
        if (el) this.productsCount.innerHTML = el.innerHTML;
      }

      renderFilters(html) {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-collection-filters]');
        if (!el) return;
        this.filters.innerHTML = el.innerHTML;
        this.filtersForm = document.querySelector('[data-collection-filters-form]');
        this.bindFilterButtonsEvents();
        this.bindToggleButtonsEvents();
      }

      updateURLHash(queryStr) {
        history.pushState(
          { searchParams: queryStr },
          '',
          `${window.location.pathname}${queryStr && '?' + queryStr}`
        );
        document.dispatchEvent(new CustomEvent('theme:collection:url-updated', { bubbles: true }));
      }

      /* ── Événements formulaire filtre ────────────────────────────────────── */

      bindFilterButtonsEvents() {
        this.inputSort.forEach(t => {
          if (!t._sortBound) {
            t.addEventListener('change', this.updateCollectionFormSortEvent);
            t._sortBound = true;
          }
        });

        if (this.filtersForm) {
          this.filtersForm.addEventListener('input', this.debouncedSubmitEvent.bind(this));
          this.filtersForm.addEventListener('theme:filter:range-update', this.updateRangeEvent);
        }

        this.collectionSortOptions?.addEventListener('keyup', this.onTabHandlerEvent);

        if (this.filterMode !== 'tag' && this.filterMode !== 'group' && this.enableFilters) {
          this.container.querySelectorAll('[data-filter-reset-button]').forEach(t => {
            t.addEventListener('click', this.onFilterResetClick, { once: true });
          });
        }
      }

      onFilterResetClick(t) {
        t.preventDefault();
        this.renderSection(new URL(t.currentTarget.href).searchParams.toString());
      }

      bindToggleButtonsEvents() {
        this.container.querySelectorAll('[data-toggle-filters]').forEach(t => {
          if (!t._toggleBound) {
            t.addEventListener('click', this.onFilterToggleClick);
            t._toggleBound = true;
          }
        });
        this.container.querySelectorAll('[data-close-filters]').forEach(t => {
          if (!t._closeBound) {
            t.addEventListener('click', this.hideFiltersDrawer);
            t._closeBound = true;
          }
        });
        this.container.querySelectorAll('[data-open-filters]').forEach(t => {
          if (!t._openBound) {
            t.addEventListener('click', this.showFiltersDrawer);
            t._openBound = true;
          }
        });
        const wrapper = this.container.querySelector('[data-collection-wrapper]');
        if (wrapper && !wrapper._keyupBound) {
          wrapper.addEventListener('keyup', this.onKeyUpHandler);
          wrapper._keyupBound = true;
        }
      }

      onTabHandler(t) {
        if (t.code !== theme.keyboardKeys.SPACE && t.code !== theme.keyboardKeys.ENTER && t.code !== theme.keyboardKeys.NUMPADENTER) return;
        const val = t.target.previousElementSibling.value;
        this.filtersForm.querySelectorAll('[data-input-sort]').forEach(input => {
          input.checked = false;
          if (input.value === val) input.checked = true;
        });
        this.filtersForm.dispatchEvent(new Event('input', { bubbles: true }));
        t.target.dispatchEvent(new Event('click', { bubbles: true }));
      }

      onKeyUpHandler(t) {
        if (t.code === theme.keyboardKeys.ESCAPE) this.hideFiltersDrawer();
      }

      onFilterToggleClick(t) {
        t.preventDefault();
        window.theme.setVars();
        this.filters.classList.contains('collection__filters--visible')
          ? this.hideFiltersDrawer()
          : this.showFiltersDrawer();
      }

      sortDropdownToggle() {
        this.collectionSortOptions?.classList.toggle('collection__sort__option-wrapper--visible');
      }

      bodyClick(t) {
        if (!this.collectionSortOptions) return;
        const onToggle = this.sortToggle.contains(t.target);
        if (this.collectionSortOptions.classList.contains('collection__sort__option-wrapper--visible') && !onToggle) {
          this.sortDropdownToggle();
        }
      }

      updateCollectionFormSort(t) {
        const el  = t.target;
        const val = el.value;
        const inOptionsPanel = el.closest('[data-collection-sort-options]');
        this.container.querySelectorAll('[data-input-sort]').forEach(input => {
          if (input.value === val) input.checked = true;
        });
        if (inOptionsPanel !== null) this.filtersForm.dispatchEvent(new Event('input', { bubbles: true }));
      }

      showFiltersDrawer() {
        this.a11y.state.trigger = document.querySelector('[data-toggle-filters]');
        this.a11y.trapFocus({ container: this.filters });
        this.mobileFiltersScrollLock();
      }

      mobileFiltersScrollLock() {
        if (window.innerWidth < theme.sizes.small) {
          const list = document.querySelector('[data-collection-filters-list]');
          if (!this.filters.classList.contains('collection__filters--visible')) {
            this.filters.classList.add('collection__filters--visible');
          }
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: list }));
        }
      }

      hideFiltersOnMobile() {
        const triggers = this.container.querySelectorAll('[data-collapsible-trigger]:not([data-show-more-trigger])');
        if (window.innerWidth < theme.sizes.small) {
          requestAnimationFrame(() => {
            triggers.forEach(t => {
              const active = t.getAttribute('data-filter-active') === 'true';
              if (t.classList.contains('is-expanded') && !active) t.dispatchEvent(new Event('click'));
            });
          });
        }
      }

      showFiltersOnDesktop() {
        const triggers    = this.container.querySelectorAll('[data-collapsible-trigger]:not([data-show-more-trigger])');
        const state       = this.container.getAttribute('data-filters-default-state');
        const isFirstOpen = state === 'first-open';
        const isOpen      = state === 'open';
        const isClosed    = state === 'closed';
        const offset      = this.enableSorting ? 1 : 0;

        triggers.forEach((t, i) => {
          const expanded = t.classList.contains('is-expanded');
          const active   = t.getAttribute('data-filter-active') === 'true';
          const forced   = active && !expanded && isOpen;

          if (active && !forced) return;

          if (
            (isClosed    && expanded)           ||
            (isFirstOpen && !expanded && i === offset) ||
            (isFirstOpen && expanded  && i !== offset) ||
            (isOpen      && !expanded)          ||
            forced
          ) {
            t.dispatchEvent(new Event('click'));
          }
        });
      }

      hideFiltersDrawer() {
        const visible  = this.filters.classList.contains('collection__filters--visible');
        const loading  = this.container.classList.contains('is-loading');
        if (visible) {
          this.filters.classList.remove('collection__filters--visible');
          this.a11y.removeTrapFocus();
        }
        if (!loading) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: 300 }));
        }
      }

      filtersResizeEvents() {
        if (window.innerWidth >= theme.sizes.small) {
          this.showFiltersOnDesktop();
          this.hideFiltersDrawer();
        } else {
          this.hideFiltersOnMobile();
        }
      }

      filterShowMore() {
        this.showMore = this.container.querySelectorAll('[data-show-more]');
        if (!this.showMore.length) return;

        this.showMore.forEach(item => {
          const trigger  = item.querySelector('[data-collapsible-trigger]');
          const actions  = item.querySelector('[data-show-more-actions]');
          if (!actions) return;

          const showMoreTrigger    = actions.querySelector('[data-show-more-trigger]');
          const showMoreContainer  = actions.querySelector('[data-show-more-container]');
          const focusableEls       = showMoreContainer.querySelectorAll(window.theme.focusable);

          if (showMoreContainer.getAttribute('aria-expanded') !== 'true') {
            focusableEls.forEach(el => el.setAttribute('tabindex', '-1'));
          }

          showMoreTrigger.addEventListener('keyup', t => {
            if (t.code === theme.keyboardKeys.SPACE || t.code === theme.keyboardKeys.ENTER || t.code === theme.keyboardKeys.NUMPADENTER) {
              this.updateShowMoreFocusableElements(t, focusableEls);
            }
          });
          showMoreTrigger.addEventListener('click', t => this.updateShowMoreFocusableElements(t, focusableEls));

          trigger.addEventListener('keyup', t => {
            if (t.code === theme.keyboardKeys.SPACE || t.code === theme.keyboardKeys.ENTER || t.code === theme.keyboardKeys.NUMPADENTER) {
              this.updateCollapsedContainerFocusableElements(trigger, showMoreTrigger, focusableEls);
            }
          });
          trigger.addEventListener('click', () => this.updateCollapsedContainerFocusableElements(trigger, showMoreTrigger, focusableEls));
        });
      }

      updateCollapsedContainerFocusableElements(trigger, showMoreTrigger, focusableEls) {
        requestAnimationFrame(() => {
          const expanded     = trigger.getAttribute('aria-expanded') === 'true';
          const moreExpanded = showMoreTrigger.getAttribute('aria-expanded') === 'true';
          focusableEls.forEach(el => {
            if (!expanded && moreExpanded)  el.setAttribute('tabindex', '-1');
            if (expanded  && moreExpanded)  el.removeAttribute('tabindex');
          });
        });
      }

      updateShowMoreFocusableElements(t, focusableEls) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const expanded = t.target.getAttribute('aria-expanded') === 'true';
            focusableEls.forEach((el, i) => {
              if (expanded) {
                el.removeAttribute('tabindex');
                if (i === 0) el.focus();
              } else {
                el.setAttribute('tabindex', '-1');
              }
            });
          });
        });
      }

      /* ── Filtres par tag / group ─────────────────────────────────────────── */

      initTagFilters() {
        if ((this.filterMode !== 'tag' && this.filterMode !== 'group') || !this.enableFilters) return;
        this.tags = this.container.dataset.tags.split('+').filter(t => t);
        this.bindFilterTagButtonsEvents();
        this.bindSortChangeEvent();
      }

      renderTagFiltersProducts(url) {
        this.startLoading();
        fetch(url)
          .then(r => r.text())
          .then(html => {
            const doc  = new DOMParser().parseFromString(html, 'text/html');
            const grid = doc.querySelector('[data-collection-products]');
            const filtersHtml = doc.querySelector('[data-collection-filters]').innerHTML;

            this.productGrid.innerHTML = grid.innerHTML;
            this.filters.innerHTML     = filtersHtml;
            this.inputSort   = this.container.querySelectorAll('[data-input-sort]');
            this.filtersForm = document.querySelector('[data-collection-filters-form]');
            this.filterData  = [...this.filterData, { html, url }];
            this.alreadyClicked = false;

            this.bindFilterTagButtonsEvents();
            this.bindFilterButtonsEvents();
            this.bindSortChangeEvent();
            this.bindToggleButtonsEvents();
            this.reInitAjaxinate(grid);
            this.initProductGridEvents();
            this.updateProductsCount(html);
            this.mobileFiltersScrollLock();
            this.hideFiltersOnMobile();
            this.filterShowMore();
            history.replaceState && window.history.pushState({ path: url }, '', url);
          })
          .catch(err => {
            this.finishLoading();
            console.log(`Error: ${err}`);
          });
      }

      bindFilterTagButtonsEvents() {
        this.container.querySelectorAll('[data-collection-filter-tag-button]').forEach(t => {
          t.addEventListener('click', this.onFilterTagButtonClick.bind(this));
        });
        this.container.querySelectorAll('[data-filter-tag-reset-button]').forEach(t => {
          t.addEventListener('click', this.onFilterTagClearClick);
        });
        this.container.querySelectorAll('[data-filter-reset-button]').forEach(t => {
          t.addEventListener('click', this.onFilterTagResetClick);
        });
      }

      bindSortChangeEvent() {
        this.container.querySelectorAll('[data-input-sort]').forEach(t => {
          t.addEventListener('input', this.debouncedSortEvent.bind(this));
        });
      }

      onFilterTagButtonClick(t) {
        t.preventDefault();
        if (this.alreadyClicked) return;
        this.alreadyClicked = true;

        const btn = t.currentTarget;
        const tag = btn.dataset.tag;

        if (btn.parentNode.classList.contains('is-active')) {
          const idx = this.tags.indexOf(tag);
          btn.parentNode.classList.remove('is-active');
          if (idx > -1) this.tags.splice(idx, 1);
        } else {
          btn.parentNode.classList.add('is-active');
          this.tags.push(tag);
        }

        const url = `${this.collectionHandle}/${this.tags.join('+')}?sort_by=${this.getSortValue()}`;
        this.container.querySelector('[data-collection-filter]').classList.remove('is-expanded');
        this.container.querySelector('[data-collection-filter]').setAttribute('aria-expanded', false);
        this.container.setAttribute('data-tags', '[' + this.tags + ']');
        this.renderTagFiltersProducts(url);
      }

      onFilterTagClearClick(t) {
        t.preventDefault();
        if (this.alreadyClicked) return;
        this.alreadyClicked = true;

        const tag = t.currentTarget.dataset.tag;
        const idx = this.tags.indexOf(tag);
        if (idx > -1) this.tags.splice(idx, 1);

        const url = `${this.collectionHandle}/${this.tags.join('+')}?sort_by=${this.getSortValue()}`;
        this.container.setAttribute('data-tags', '[' + this.tags + ']');
        this.renderTagFiltersProducts(url);
      }

      onSortChange() {
        const url = `${this.collectionHandle}/${this.tags.join('+')}?sort_by=${this.getSortValue()}`;
        this.renderTagFiltersProducts(url);
      }

      getSortValue() {
        let val = '';
        this.inputSort.forEach(t => { if (t.checked) val = t.value; });
        return val;
      }

      onFilterTagResetClick(t) {
        t?.preventDefault();
        if (this.alreadyClicked) return;
        this.alreadyClicked = true;

        this.container.querySelectorAll('[data-collection-filter-tag]').forEach(el => el.classList.remove('is-active'));
        this.container.querySelectorAll('[data-collection-filter]').forEach(el => {
          el.classList.remove('is-expanded');
          el.setAttribute('aria-expanded', false);
        });
        this.tags = [];
        this.container.setAttribute('data-tags', '');

        const url = `${this.collectionHandle}/?sort_by=${this.getSortValue()}`;
        this.renderTagFiltersProducts(url);
      }

      /* ── Divers ──────────────────────────────────────────────────────────── */

      handleVisibleTooltips() {
        const tooltip = document.querySelector('[data-tooltip-container]');
        tooltip?.classList.contains('is-visible') && tooltip.classList.remove('is-visible');
      }

      updateRange() {
        const seMin    = this.filtersForm.querySelector('[data-se-min-value]');
        const seMax    = this.filtersForm.querySelector('[data-se-max-value]');
        const fieldMin = this.filtersForm.querySelector('[data-field-price-min]');
        const fieldMax = this.filtersForm.querySelector('[data-field-price-max]');

        if (!seMin?.hasAttribute('data-se-min-value') || !seMax?.hasAttribute('data-se-max-value')) return;

        const defaultMin  = parseFloat(fieldMin.placeholder, 10);
        const defaultMax  = parseFloat(fieldMax.placeholder, 10);
        const currentMin  = parseFloat(seMin.getAttribute('data-se-min-value'), 10);
        const currentMax  = parseFloat(seMax.getAttribute('data-se-max-value'), 10);

        if (defaultMin !== currentMin || defaultMax !== currentMax) {
          fieldMin.value = parseInt(currentMin);
          fieldMax.value = parseInt(currentMax);
          this.filtersForm.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const formData = new FormData(this.filtersForm);
        const params   = new URLSearchParams(formData);
        const excluded = [];

        if (this.isSearchPage) {
          this.searchForm  = this.container.querySelector('[data-search-form]');
          this.currentType = this.container.getAttribute('data-current-type');
        }

        // Plage de prix : supprimer si aux valeurs par défaut
        const seMin    = this.filtersForm.querySelector('[data-se-min-value]');
        const seMax    = this.filtersForm.querySelector('[data-se-max-value]');
        const fieldMin = this.filtersForm.querySelector('[data-field-price-min]');
        const fieldMax = this.filtersForm.querySelector('[data-field-price-max]');

        if (seMin && seMax && fieldMin && fieldMax && seMin.hasAttribute('data-se-min') && seMax.hasAttribute('data-se-max')) {
          const defMin = parseFloat(seMin.getAttribute('data-se-min'), 10);
          const defMax = parseFloat(seMax.getAttribute('data-se-max'), 10);
          const curMin = fieldMin.value ? parseFloat(fieldMin.value, 10) : defMin;
          const curMax = fieldMax.value ? parseFloat(fieldMax.value, 10) : defMax;

          if (curMin <= defMin && curMax >= defMax) {
            excluded.push('filter.v.price.gte');
            excluded.push('filter.v.price.lte');
            params.delete('filter.v.price.gte');
            params.delete('filter.v.price.lte');
          }
        }

        let queryStr = params.toString();

        if (this.isSearchPage) {
          queryStr = this._mergeSearchParams(this.searchForm, this.filtersForm, excluded);
          let appendType = '';
          if (this.currentType === 'all') appendType = '&type=product';
          if (queryStr.indexOf('&type=product') > -1) appendType = '';
          queryStr += appendType;
        }

        this.renderSection(queryStr, evt);
      }

      _mergeSearchParams(searchForm, filtersForm, excluded = [], forceType = false) {
        const searchData   = new FormData(searchForm);
        const searchParams = new URLSearchParams(searchData);
        if (!filtersForm) return searchParams.toString();

        const filterData   = new FormData(filtersForm);
        const filterParams = new URLSearchParams(filterData);
        const empty        = [];

        for (const [k, v] of searchData.entries()) if (v === '') empty.push(k);
        for (const [k, v] of filterData.entries()) if (v === '') empty.push(k);

        empty.forEach(k => {
          if (searchParams.has(k)) searchParams.delete(k);
          if (filterParams.has(k)) filterParams.delete(k);
        });

        for (const k of searchParams.keys()) {
          if (filterParams.has(k)) filterParams.delete(k);
        }

        excluded.forEach(k => {
          if (searchParams.has(k)) searchParams.delete(k);
          if (filterParams.has(k)) filterParams.delete(k);
        });

        if (forceType) {
          if (filterParams.has('type')) filterParams.delete('type');
          searchParams.set('type', forceType);
        }

        return `${searchParams.toString()}&${filterParams.toString()}`;
      }

      getProductsOffsetTop() {
        return this.productGrid.getBoundingClientRect().top
          - document.body.getBoundingClientRect().top
          - this.filtersStickyBar.offsetHeight;
      }

      getStickyBarOffsetTop() {
        return this.filtersStickyBar.getBoundingClientRect().top
          - document.body.getBoundingClientRect().top;
      }

      startLoading() {
        this.container.classList.add('is-loading');
        if (window.innerWidth >= theme.sizes.small) {
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
        }
        window.scrollTo({ top: this.getProductsOffsetTop(), left: 0, behavior: 'smooth' });
      }

      finishLoading() {
        const popups  = document.querySelectorAll('[data-section-type="popups"] .popup--visible');
        const hasPopup = popups.length > 0;

        this.container.classList.remove('is-loading');

        if (hasPopup) {
          let noLock = 0;
          [...popups].forEach(p => { if (p.hasAttribute('data-prevent-scroll-lock')) noLock++; });
          if (noLock === popups.length) {
            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: 300 }));
          }
        } else if (window.innerWidth >= theme.sizes.small) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: 300 }));
        }
      }

      disconnectedCallback() {
        if (this.filters) document.removeEventListener('theme:resize:width', this.resizeEvent);
        window.removeEventListener('popstate', this.onHistoryChangeBound);
        document.removeEventListener('click', this.bodyClickEvent);
        if (this.groupTagFilters.length > 0) this.onFilterTagResetClick();
        this.finishLoading();
      }

    });
  }

})();
